import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Confetti, MapPin, Clock, Plus, X, Ticket, Printer, Users, Share, GameController, Trophy, ArrowRight, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";
import MultiImageUploader from "@/components/MultiImageUploader";
import MyCoupons from "@/pages/MyCoupons";
import { Link } from "react-router-dom";

export default function Events() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming"); // upcoming | mine | tickets
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const loadAll = async () => {
    try { const { data } = await api.get("/events"); setEvents(data); } catch (err) { console.error("load events failed", err); }
    if (!user || user === false) return;
    try { const { data } = await api.get("/events", { params: { mine: true } }); setMyEvents(data); } catch (err) { console.error("load my events failed", err); }
    try { const { data } = await api.get("/events/rsvps/mine"); setTickets(data); } catch (err) { console.error("load tickets failed", err); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  const canOrganize = user && user !== false && (user.user_type === "organizer" || user.role === "admin");

  const rsvp = async (eid) => {
    try { const { data } = await api.post("/events/rsvp", { event_id: eid }); toast.success(`RSVP confirmed · ${data.ticket_number}`); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Community events</div>
          <h1 className="font-heading text-5xl mt-2 tracking-tight">What&apos;s happening <em className="text-[#2C4A3B] not-italic">around</em>.</h1>
          <p className="text-[#5C564D] mt-2 max-w-2xl">Cultural gatherings, workshops, meet-ups. RSVP once, get a printable pass, walk in.</p>
        </div>
        {(canOrganize || user?.role === "admin") && (
          <button data-testid="btn-new-event" onClick={() => setShowForm(true)} className="samaaj-btn-primary text-sm flex items-center gap-1"><Plus size={14} weight="bold" /> Post event</button>
        )}
      </div>

      <div className="mt-8 flex gap-2 border-b border-[#E5D9CC]">
        {[["upcoming", "Upcoming"], ["mine", canOrganize ? "My events" : null], ["tickets", "My tickets"], ["play", "Play & Earn"], ["coupons", "My coupons"]].filter(([_, l]) => l).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ev-tab-${k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-[#2C4A3B] text-[#2C4A3B]" : "border-transparent text-[#5C564D]"}`}>{l}</button>
        ))}
      </div>

      {tab === "upcoming" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-grid">
          {events.length === 0 ? (
            <div className="samaaj-card p-10 col-span-full text-center text-[#5C564D]">No upcoming events yet.</div>
          ) : events.map((e) => <EventCard key={e.id} e={e} onRsvp={rsvp} user={user} />)}
        </div>
      )}
      {tab === "mine" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-events">
          {myEvents.length === 0 ? (
            <div className="samaaj-card p-10 col-span-full text-center text-[#5C564D]">You haven&apos;t posted any events yet.</div>
          ) : myEvents.map((e) => <EventCard key={e.id} e={e} onRsvp={rsvp} user={user} showStatus />)}
        </div>
      )}
      {tab === "tickets" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-tickets">
          {tickets.length === 0 ? (
            <div className="samaaj-card p-10 col-span-full text-center text-[#5C564D]">You have no tickets yet.</div>
          ) : tickets.map((t) => (
            <div key={t.id} className="samaaj-card p-5" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center gap-2">
                <Ticket size={18} className="text-[#2C4A3B]" weight="duotone" />
                <span className="samaaj-chip text-[10px] uppercase">{t.ticket_number}</span>
              </div>
              <div className="font-heading text-xl mt-2">{t.event_title}</div>
              <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><Clock size={12} /> {new Date(t.event_date).toLocaleString()}</div>
              <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><MapPin size={12} /> {t.event_venue}</div>
              <div className="mt-3 flex justify-end">
                <Link to={`/events/ticket/${t.id}/print`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print pass</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <EventForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); loadAll(); toast.success("Submitted — pending admin approval"); }} />}
      {tab === "play" && (
        <div className="mt-6 samaaj-card p-8 text-center" data-testid="play-earn-section">
          <GameController size={40} weight="duotone" className="mx-auto text-[#2C4A3B]" />
          <div className="font-heading text-3xl mt-2">Play &amp; Earn</div>
          <p className="text-sm text-[#5C564D] mt-2 max-w-lg mx-auto">Score a perfect round on the community quiz to win a $5 coupon usable at any Anveshan marketplace listing.</p>
          <Link to="/games" className="samaaj-btn-primary mt-4 inline-flex items-center gap-2"><Trophy size={14} weight="fill" /> Play the quiz <ArrowRight size={14} /></Link>
        </div>
      )}
      {tab === "coupons" && (
        <div className="mt-6" data-testid="coupons-tab">
          <MyCoupons />
        </div>
      )}
    </div>
  );
}

function EventCard({ e, onRsvp, user, showStatus }) {
  const isOrganizer = user && user !== false && user.id === e.organizer_id;
  return (
    <div className="samaaj-card overflow-hidden" data-testid={`event-${e.id}`}>
      <Link to={`/e/${e.id}`} className="block aspect-[4/3] w-full bg-[#F2EBE5] overflow-hidden">
        {e.poster_url ? <img src={e.poster_url} alt={e.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Confetti size={48} weight="duotone" className="text-[#2C4A3B]" /></div>}
      </Link>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className="samaaj-chip text-[10px] uppercase">{e.category || "community"}</span>
          {showStatus && <span className={`text-[10px] font-bold ${e.approval_status === "approved" ? "text-emerald-700" : e.approval_status === "rejected" ? "text-red-600" : "text-[#D49A36]"}`}>{e.approval_status}</span>}
        </div>
        <Link to={`/e/${e.id}`} className="block font-heading text-xl mt-2 text-[#1C1B1A] hover:text-[#2C4A3B]">{e.title}</Link>
        <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><Clock size={12} /> {new Date(e.event_date).toLocaleString()}</div>
        <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><MapPin size={12} /> {e.venue}</div>
        <p className="text-sm text-[#5C564D] mt-2 line-clamp-3">{e.description}</p>
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-heading text-lg text-[#D95A41]">{e.ticket_price ? `$${e.ticket_price}` : "Free"}</div>
          <div className="flex gap-1">
            <Link to={`/e/${e.id}`} data-testid={`btn-view-${e.id}`} className="samaaj-btn-outline text-xs flex items-center gap-1"><Share size={12} /> View / share</Link>
            {isOrganizer && e.approval_status === "approved" && (
              <Link to={`/events/${e.id}/roster`} data-testid={`btn-roster-${e.id}`} className="samaaj-btn-outline text-xs flex items-center gap-1"><Users size={12} /> Roster</Link>
            )}
            {user && user !== false && !isOrganizer && e.approval_status === "approved" && (
              <button onClick={() => onRsvp(e.id)} data-testid={`btn-rsvp-${e.id}`} className="samaaj-btn-primary text-xs">RSVP</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventForm({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", event_date: "", venue: "", ticket_price: 0, capacity: 0, contact_phone: "", category: "community" });
  const [poster, setPoster] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/events", {
        ...f,
        event_date: new Date(f.event_date).toISOString(),
        ticket_price: Number(f.ticket_price) || 0,
        capacity: Number(f.capacity) || 0,
        poster_url: poster[0] || "",
      });
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">Post an event</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input required placeholder="Event title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ev-title" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea required rows={4} placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ev-desc" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="datetime-local" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} data-testid="ev-date" className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
            <input required placeholder="Venue" value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} data-testid="ev-venue" className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
            <input type="number" step="0.01" placeholder="Ticket price ($)" value={f.ticket_price} onChange={(e) => setF({ ...f, ticket_price: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
            <input type="number" placeholder="Capacity (0 = unlimited)" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
            <input placeholder="Contact phone" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
              <option value="community">Community</option><option value="cultural">Cultural</option><option value="workshop">Workshop</option>
              <option value="sports">Sports</option><option value="religious">Religious</option>
            </select>
          </div>
          <MultiImageUploader value={poster} onChange={setPoster} max={1} testid="poster" label="Poster image" />
          <button data-testid="ev-submit" disabled={busy} className="samaaj-btn-primary w-full">{busy ? "Sending…" : "Submit for admin approval"}</button>
        </form>
      </div>
    </div>
  );
}
